/**
 * Social media service — mirrors googleCalendarService.ts patterns.
 */
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

export type Platform = 'linkedin' | 'facebook' | 'instagram' | 'twitter';
export type PostStatus = 'draft' | 'scheduled' | 'published' | 'failed' | 'cancelled';

export interface SocialStatus {
  app_configured: boolean;
  connected: boolean;
  handle: string | null;
  expiring_soon?: boolean;
}

export interface SocialPost {
  id: string;
  coach_id: string;
  platform: Platform;
  content: string;
  image_r2_key: string | null;
  image_url: string | null;
  status: PostStatus;
  scheduled_at: string | null;
  published_at: string | null;
  platform_post_id: string | null;
  error_message: string | null;
  ai_generated: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreatePostPayload {
  platforms: Platform[];
  content: string;
  image_url?: string;
  image_r2_key?: string;
  scheduled_at?: string;
  ai_generated?: boolean;
}

export interface AIDraftRequest {
  platform: Platform;
  topic?: string;
  ef_domain?: string;
  free_text?: string;
}

export interface ImageUploadResponse {
  r2_key: string;
  image_url: string;
}

function apiUrl(path: string): string {
  return `${config.apiBaseUrl}${path}`;
}

function authHeaders(): Record<string, string> {
  const userData = localStorage.getItem('userData');
  const token = userData ? JSON.parse(userData).token || JSON.parse(userData)._token : null;
  // also check direct token storage
  const directToken = localStorage.getItem('token');
  const finalToken = token || directToken;
  return finalToken
    ? { Authorization: `Bearer ${finalToken}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

export async function getSocialStatus(platform: Platform): Promise<SocialStatus> {
  const resp = await fetch(apiUrl(endpoints.main.social.status(platform)), {
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error(`Failed to get ${platform} status`);
  return resp.json();
}

export async function getSocialAuthUrl(platform: Platform): Promise<string> {
  const resp = await fetch(apiUrl(endpoints.main.social.authUrl(platform)), {
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error(`Failed to get ${platform} auth URL`);
  const data = await resp.json();
  return data.auth_url;
}

export async function disconnectSocial(platform: Platform): Promise<void> {
  const resp = await fetch(apiUrl(endpoints.main.social.disconnect(platform)), {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error(`Failed to disconnect ${platform}`);
}

export async function createPosts(payload: CreatePostPayload): Promise<SocialPost[]> {
  const resp = await fetch(apiUrl(endpoints.main.social.posts), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as any).detail || 'Failed to create post');
  }
  return resp.json();
}

export async function listPosts(platform?: Platform, status?: PostStatus): Promise<SocialPost[]> {
  const params = new URLSearchParams();
  if (platform) params.set('platform', platform);
  if (status) params.set('status', status);
  const url = apiUrl(endpoints.main.social.posts) + (params.toString() ? `?${params}` : '');
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) throw new Error('Failed to list posts');
  return resp.json();
}

export async function deletePost(id: string): Promise<void> {
  const resp = await fetch(apiUrl(endpoints.main.social.post(id)), {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error('Failed to delete post');
}

export async function uploadImage(file: File): Promise<ImageUploadResponse> {
  const headers = authHeaders();
  // Remove Content-Type so browser sets multipart boundary
  delete headers['Content-Type'];
  const form = new FormData();
  form.append('file', file);
  const resp = await fetch(apiUrl(endpoints.main.social.uploadImage), {
    method: 'POST',
    headers,
    body: form,
  });
  if (!resp.ok) throw new Error('Failed to upload image');
  return resp.json();
}

export async function deleteImage(r2Key: string): Promise<void> {
  const resp = await fetch(apiUrl(endpoints.main.social.deleteImage(r2Key)), {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!resp.ok) throw new Error('Failed to delete image');
}

export async function generateAIDraft(payload: AIDraftRequest): Promise<{ draft: string; platform: Platform }> {
  const resp = await fetch(apiUrl(endpoints.main.social.aiDraft), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as any).detail || 'AI draft failed');
  }
  return resp.json();
}
